// Pass Map widget (Players canvas) — a custom SVG full pitch with one line per
// pass (start→end), coloured by completion, with key passes / assists picked out
// on top. No `soccer-vis` (§7B decision 1). Sibling to the Shot Map; the maths
// lives in the pure, unit-tested `passMap.ts` and this file is the view.
//
// Consumes player + competition + season. A busy passer is 1000+ lines — SVG
// handles it; to keep the DOM light we attach hover tooltips only to the
// highlighted passes (key passes + assists), not every bulk line.

import type { PlayerEvent } from "@catbento/shared";
import { usePlayerPasses } from "../data/players.js";
import type { WidgetScope } from "./scope.js";
import { theme } from "../lib/theme.js";
import {
  VIEW,
  PASS_COLOR,
  classifyPass,
  isPlottable,
  summarizePasses,
  type PassClass,
} from "./passMap.js";

const LINE = "rgba(148,163,184,0.30)"; // faint pitch markings

/** Stroke style per pass class. Bulk complete/incomplete are thin + faint; key
 *  passes and assists are bright, thicker, and drawn last so they sit on top. */
function strokeFor(c: PassClass): { color: string; width: number; opacity: number } {
  if (c.assist) return { color: PASS_COLOR.assist, width: 1.0, opacity: 1 };
  if (c.keyPass) return { color: PASS_COLOR.keyPass, width: 0.8, opacity: 0.95 };
  if (c.completion === "complete") return { color: PASS_COLOR.complete, width: 0.35, opacity: 0.28 };
  return { color: PASS_COLOR.incomplete, width: 0.35, opacity: 0.34 };
}

/** Highlighted passes (assist > key pass) draw after the bulk so they're on top. */
function drawOrder(c: PassClass): number {
  return c.assist ? 2 : c.keyPass ? 1 : 0;
}

export default function PassMapWidget({ scope }: { scope: WidgetScope }) {
  const { playerId, competitionId, seasonId } = scope;
  const { data, isPending, isError, error } = usePlayerPasses(playerId, competitionId, seasonId);

  if (playerId === null) return <Msg>Search for a player above.</Msg>;
  if (competitionId === null || seasonId === null)
    return <Msg>Pick a competition and season above.</Msg>;
  if (isPending) return <Msg>Loading passes…</Msg>;
  if (isError) return <Msg danger>Couldn't load passes. {String(error)}</Msg>;

  const passes = data.passes;
  if (passes.length === 0) return <Msg>No passes for this player in this season.</Msg>;

  const summary = summarizePasses(passes);
  const plottable = passes.filter(isPlottable);
  const dropped = passes.length - plottable.length;

  // Pre-classify once, then order so highlights render on top.
  const items = plottable
    .map((pass) => ({ pass, cls: classifyPass(pass) }))
    .sort((a, b) => drawOrder(a.cls) - drawOrder(b.cls));

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: 8 }}>
      <div style={{ color: theme.inkMuted, fontSize: 13, fontVariantNumeric: "tabular-nums" }}>
        <span style={{ color: theme.ink, fontWeight: 600 }}>{summary.total}</span> passes ·{" "}
        <span style={{ color: theme.ink, fontWeight: 600 }}>{summary.completionPct}%</span> complete
        {dropped > 0 && <span style={{ color: theme.inkFaint }}> · {dropped} without location</span>}
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex", justifyContent: "center" }}>
        <svg
          viewBox={`-3 -3 ${VIEW.width + 6} ${VIEW.height + 6}`}
          preserveAspectRatio="xMidYMid meet"
          style={{ width: "100%", height: "100%", maxHeight: "100%" }}
          role="img"
          aria-label={`Pass map: ${summary.total} passes, ${summary.completionPct}% complete`}
        >
          <Pitch />
          <g strokeLinecap="round">
            {items.map(({ pass, cls }) => {
              const s = strokeFor(cls);
              const highlight = cls.assist || cls.keyPass;
              return (
                <line
                  key={pass.id}
                  x1={pass.x as number}
                  y1={pass.y as number}
                  x2={pass.xEnd as number}
                  y2={pass.yEnd as number}
                  stroke={s.color}
                  strokeWidth={s.width}
                  strokeOpacity={s.opacity}
                >
                  {/* Tooltip only on the few highlighted passes — keeps the DOM light. */}
                  {highlight && <title>{passTitle(pass, cls)}</title>}
                </line>
              );
            })}
          </g>
        </svg>
      </div>

      <Legend summary={summary} />
    </div>
  );
}

/** Full StatsBomb pitch (120×80), attack left→right. Both penalty boxes / 6-yard
 *  boxes / spots + halfway line + centre circle — passes come from everywhere. */
function Pitch() {
  return (
    <g fill="none" stroke={LINE} strokeWidth={0.4}>
      <rect x={0} y={0} width={VIEW.width} height={VIEW.height} fill={theme.tile} />
      {/* Halfway line + centre circle + spot. */}
      <line x1={60} y1={0} x2={60} y2={80} />
      <circle cx={60} cy={40} r={10} />
      <circle cx={60} cy={40} r={0.6} fill={LINE} stroke="none" />
      {/* Left penalty area + 6-yard + spot. */}
      <rect x={0} y={18} width={18} height={44} />
      <rect x={0} y={30} width={6} height={20} />
      <circle cx={12} cy={40} r={0.6} fill={LINE} stroke="none" />
      {/* Right penalty area + 6-yard + spot. */}
      <rect x={102} y={18} width={18} height={44} />
      <rect x={114} y={30} width={6} height={20} />
      <circle cx={108} cy={40} r={0.6} fill={LINE} stroke="none" />
      {/* Goals (thin bars just outside each goal line). */}
      <rect x={-1.2} y={36} width={1.2} height={8} fill={theme.inkFaint} stroke="none" />
      <rect x={120} y={36} width={1.2} height={8} fill={theme.inkFaint} stroke="none" />
    </g>
  );
}

/** Native hover tooltip for a highlighted pass: minute, kind, complete/incomplete. */
function passTitle(pass: PlayerEvent, c: PassClass): string {
  const parts: string[] = [];
  if (pass.minute !== null) parts.push(`${pass.minute}'`);
  parts.push(c.assist ? "Assist" : "Key pass");
  parts.push(c.completion === "complete" ? "Complete" : "Incomplete");
  return parts.join(" · ");
}

function Legend({
  summary,
}: {
  summary: { complete: number; incomplete: number; keyPasses: number; assists: number };
}) {
  const items: { key: keyof typeof PASS_COLOR; label: string; count: number }[] = [
    { key: "complete", label: "Complete", count: summary.complete },
    { key: "incomplete", label: "Incomplete", count: summary.incomplete },
    { key: "keyPass", label: "Key pass", count: summary.keyPasses },
    { key: "assist", label: "Assist", count: summary.assists },
  ];
  return (
    <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: theme.inkMuted }}>
      {items.map(({ key, label, count }) => (
        <span key={key} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
          <span style={{ width: 12, height: 2, background: PASS_COLOR[key], display: "inline-block" }} />
          {label} <span style={{ color: theme.inkFaint }}>{count}</span>
        </span>
      ))}
    </div>
  );
}

function Msg({ children, danger }: { children: React.ReactNode; danger?: boolean }) {
  return <div style={{ color: danger ? theme.danger : theme.inkMuted, fontSize: 13 }}>{children}</div>;
}
